import styled, { useTheme } from "styled-components";

import { OrderIdentityVerification } from "src/contexts/documentationArea/domain/OrderIdentityVerification";
import { IDVOrderBoxHeader } from "./header/IDVOrderBoxHeader";
import { IDVOrderBoxUploader } from "./uploader/IDVOrderBoxUploader";
import { IdentityVerificationDocumentSides } from "src/contexts/identityVerification/domain/IdentityVerificationDocumentSides";
import { MEDIA_MOBILE_BREAKPOINT } from "src/apps/config/constants";
import { OrderBoxResponseItem } from "./responses/OrderBoxResponseItem";
import { OrderResponse } from "src/contexts/documentationArea/domain/OrderResponse";
import { OrderResponseStatus } from "src/contexts/documentationArea/domain/OrderResponseStatus";
import { v4 } from "uuid";
import { DragEvent, useRef, useState } from "react";
import { FadeInAnimation } from "src/apps/components/fadeIn/FadeInAnimation";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { TextContent } from "src/apps/components/typography/Texts";
import { T } from "src/apps/components/i18n/Translation";
import { useUploader } from "src/apps/hooks/uploadFiles/useUploader";
import { colors } from "@permisso/permisso-components";

interface OrderBoxProps {
  side: IdentityVerificationDocumentSides;
  order: OrderIdentityVerification;
}

export const IdentityVerificationOrderBox = ({ side, order }: OrderBoxProps) => {
  const { blackColor } = useTheme();
  const [isDragOver, setIsDragOver] = useState(false);
  const dragTimeoutRef = useRef<NodeJS.Timeout>();
  const { convertAndUploadIdentityVerificationFiles } = useUploader();

  // This is a hack to make the staged file look like a response
  const response = new OrderResponse({
    id: v4(),
    inputFiles: order.getStagedFileFromSide(side) ? [order.getStagedFileFromSide(side)!] : [],
    outputFiles: [],
    status: OrderResponseStatus.VALID,
    createdAt: new Date(),
    statusMessages: [],
    retrievalMethodOutput: null,
  });

  const supportsFileDrag = (event: DragEvent<HTMLDivElement>) =>
    Array.from(event.dataTransfer.types).includes("Files");

  const canDrop = order.canManuallyUpload() && !order.getStagedFileFromSide(side);

  const onDragEnter = (event: DragEvent<HTMLDivElement>) => {
    if (!canDrop || !supportsFileDrag(event)) return;
    event.preventDefault();
    // Cancel any pending drag-leave timeout
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    setIsDragOver(true);
  };

  const onDragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!canDrop || !supportsFileDrag(event)) return;
    event.preventDefault();
    // Cancel any pending drag-leave timeout
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    setIsDragOver(true);
  };

  const onDragLeave = (event: DragEvent<HTMLDivElement>) => {
    if (!canDrop) return;
    event.preventDefault();
    // Debounce drag-leave with a small timeout to avoid flicker from nested elements
    dragTimeoutRef.current = setTimeout(() => {
      setIsDragOver(false);
    }, 50);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    if (!canDrop || !supportsFileDrag(event)) return;
    // Clear any pending drag-leave timeout
    if (dragTimeoutRef.current) clearTimeout(dragTimeoutRef.current);
    setIsDragOver(false);

    if (event.defaultPrevented) return;

    event.preventDefault();
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length === 0) return;
    convertAndUploadIdentityVerificationFiles(order.getId(), side, files);
  };

  return (
    <OrderBoxContainer
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isDragOver ? (
        <DragOverlay>
          <FadeInAnimation duration={0.2}>
            <HoveredContainer $borderColor={blackColor}>
              <Icon size={ICON_SIZES.s14} color={blackColor} path={ICON_PATHS.PLUS_LARGE} />
              <TextContent $color={blackColor}>
                <T k="documentation_area.uploader_box.upload_your_documents" />
              </TextContent>
            </HoveredContainer>
          </FadeInAnimation>
        </DragOverlay>
      ) : null}
      <IDVOrderBoxHeader order={order} side={side} />
      {!order.getStagedFileFromSide(side) ? (
        <IDVOrderBoxUploader
          order={order}
          side={side}
          isDragOver={isDragOver}
          onDragEnd={() => setIsDragOver(false)}
        />
      ) : null}
      {order.getStagedFileFromSide(side) ? (
        <OrderBoxResponseItem response={response} orderType={order.getType()} $isHidden={false} />
      ) : null}
    </OrderBoxContainer>
  );
};

const OrderBoxContainer = styled.div`
  position: relative;
  gap: 1rem;
  display: flex;
  flex-direction: column;
  border: 0.0625rem solid ${colors.grey400};
  border-radius: 0.5rem;
  padding: 0.75rem;
  @media (max-width: ${MEDIA_MOBILE_BREAKPOINT}rem) {
    padding: 0rem;
    border: unset;
  }
`;

const DragOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: 0.5rem;
  pointer-events: none;
  display: flex;
  align-items: stretch;
  background-color: rgba(255, 255, 255, 0.7);
`;

const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: row;
  height: 4.6875rem;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 0.375rem;
`;

const HoveredContainer = styled(Container)<{ $borderColor: string }>`
  height: auto;
  background-color: ${colors.grey300};
  border: 1px dashed ${(props) => props.$borderColor};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;
